import { Box, Grid, GridItem, ToastId } from '@chakra-ui/react';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { LanguageContext } from '../../context/LanguageContextProvider';
import { WindowSizeContext } from '../../context/WindowSizeContextProvider';
import { commonStyle } from '../../common/styles';
import { DialogStateContext } from '../../context/DialogStateContextProvider';
import { FliterContext } from '../../context/FliterContextProvider';
import { MessageInfoContext } from '../../context/MessageInfoContextProvider';
import { useToast } from '@chakra-ui/react';
import ControlButton from './ControlButton';
import { TransitsContext } from '../../context/TransitsContextProvider';
import { MasterContext } from '../../context/MasterContextProvider';
import { findNearestStation } from '../../common/userLocation';
import { calcImageSize } from '../../common/image';

const renderToast = (message: string, fontSize?: number) => {
  return (
    <Box
      bg='white'
      color='black'
      marginBottom='15%'
      fontSize={fontSize ?? '16px'}
      fontWeight='bold'
      borderRadius='5px'
      whiteSpace='pre-wrap'
      sx={{ ...commonStyle.center }}
    >
      {message}
    </Box>
  );
};

const GPS_ERROR_MESSAGE =
  '位置情報が取得できません。\n詳しくはヘルプをご覧ください。';
const GPS_FAR_MESSAGE =
  '最寄り駅までの距離が遠すぎます。\n詳しくはヘルプをご覧ください。';

const GPS_ERROR_MESSAGE_EN =
  'Location information cannot be acquired.\nPlease see Help for more information.';
const GPS_FAR_MESSAGE_EN = 'Distance to the nearest streetcar stop is too far.';

type Props = {
  reset: () => void; //全体図ボタン
  toCurrent: (stationNo: string) => void; //現在地：ユーザーの最寄り電停確定後のイベント
};

const Controls: React.FC<Props> = (props) => {
  const { reset, toCurrent } = props;
  const { language, setLanguage } = useContext(LanguageContext);
  const { openDialog } = useContext(DialogStateContext);
  const { filter, setFilter } = useContext(FliterContext);
  const { size } = useContext(WindowSizeContext);
  const { master } = useContext(MasterContext);
  const { updateMessage } = useContext(MessageInfoContext);
  const { updateTransits } = useContext(TransitsContext);

  // 画像を領域に配置したときのサイズ
  const { imageWidth: iw1, imageHeight: ih1 } = useMemo(
    () => calcImageSize(size.w * 0.17, size.h * 0.06, 1239, 655),
    [size.h, size.w]
  );
  const { imageWidth: iw2, imageHeight: ih2 } = useMemo(
    () => calcImageSize(size.w * 0.12, size.h * 0.065, 655, 655),
    [size.h, size.w]
  );

  const toast = useToast({
    duration: 1500,
    containerStyle: {
      pointerEvents: 'none'
    }
  });
  const toastRef = useRef<ToastId>(); //表示中のトーストID

  // ユーザーの位置情報を取得して遷移
  const toCurrentLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      // 正常時
      (position) => {
        const _stationNo = findNearestStation(
          master.stations,
          position.coords.latitude,
          position.coords.longitude
        );
        if (_stationNo !== null) toCurrent(_stationNo);
        // 最寄り電停との距離が遠すぎる場合は遷移しない
        else {
          if (toastRef.current) {
            toast.close(toastRef.current);
          }
          toastRef.current = toast({
            render: () =>
              renderToast(
                language === 'en' ? GPS_FAR_MESSAGE_EN : GPS_FAR_MESSAGE
              )
          });
        }
      },

      // 失敗時
      () => {
        if (toastRef.current) {
          toast.close(toastRef.current);
        }
        toastRef.current = toast({
          render: () =>
            renderToast(
              language === 'en' ? GPS_ERROR_MESSAGE_EN : GPS_ERROR_MESSAGE
            )
        });
      }
    );
  }, [language, master.stations, toCurrent, toast]);

  return (
    <Grid
      w='100%'
      h='100%'
      gridTemplateAreas={`'... update overview current filter language help ...'`}
      gridTemplateColumns='1% repeat(4, 18%) repeat(2, 13%) 1%'
      gridTemplateRows='100%'
      bgColor='white'
    >
      {/* 更新 */}
      <GridItem gridArea='update' sx={{ ...commonStyle.center }}>
        <ControlButton
          language={language}
          name='SvgReload'
          width={iw1.toString()}
          height={ih1.toString()}
          onClick={() => {
            updateMessage();
            updateTransits();
          }}
        ></ControlButton>
      </GridItem>

      {/* 全体図 */}
      <GridItem gridArea='overview' sx={{ ...commonStyle.center }}>
        <ControlButton
          language={language}
          name='SvgOverview'
          width={iw1.toString()}
          height={ih1.toString()}
          onClick={() => {
            reset();
          }}
        ></ControlButton>
      </GridItem>

      {/* 現在地 */}
      <GridItem gridArea='current' sx={{ ...commonStyle.center }}>
        <ControlButton
          language={language}
          name='SvgHere'
          width={iw1.toString()}
          height={ih1.toString()}
          onClick={() => {
            toCurrentLocation();
          }}
        ></ControlButton>
      </GridItem>

      {/* フィルタ */}
      <GridItem gridArea='filter' sx={{ ...commonStyle.center }}>
        <ControlButton
          language={language}
          name={filter === 'LowFloor' ? 'SvgDispAll' : 'SvgOnlyLowFloor'}
          width={iw1.toString()}
          height={ih1.toString()}
          onClick={() => {
            setFilter((pre) => (pre === 'AllTrain' ? 'LowFloor' : 'AllTrain'));
          }}
        ></ControlButton>
      </GridItem>

      {/* 言語切り替え */}
      <GridItem
        gridArea='language'
        sx={{ ...commonStyle.center, justifyContent: 'flex-end' }}
      >
        <ControlButton
          language={language}
          name='SvgLanguageSwitch'
          width={iw2.toString()}
          height={ih2.toString()}
          onClick={() => {
            setLanguage((pre) => (pre === 'ja' ? 'en' : 'ja'));
          }}
        ></ControlButton>
      </GridItem>

      {/* ヘルプ */}
      <GridItem
        gridArea='help'
        sx={{ ...commonStyle.center, justifyContent: 'flex-end' }}
      >
        <ControlButton
          language={language}
          name='SvgHelp'
          width={iw2.toString()}
          height={ih2.toString()}
          onClick={() => {
            openDialog('help');
          }}
        ></ControlButton>
      </GridItem>
    </Grid>
  );
};

export default React.memo(Controls);
